import { FormattedMessage, FormattedNumber } from '@liveauctioneers/hammer-ui-core/intl';
import { getCatalogAddress } from '@/redux/modules/catalog/catalogs/catalog.selectors';
import {
    getCatalogDetailFreeLocalPickup,
    getCatalogDetailHasBidderShippingAvailable,
    getCatalogDetailInHouseShipping,
} from '@/redux/modules/catalog/detail/catalogDetail.selectors';
import { getInvoiceById } from '@/redux/modules/checkout/checkout.selectors';
import { ShippingMethods } from '@/enums/shippingMethod';
import { ShippingQuote } from '@/types/ShippingQuote';
import { useAppSelector } from '@/redux/hooks';
import Link from '@liveauctioneers/caterwaul-components/lib/Link/Link';
import LiveShippingShippingMethod from './LiveShipping/LiveShippingShippingMethod';
import Radio from '@liveauctioneers/caterwaul-components/lib/Radio/Radio';
import Radios from '@liveauctioneers/caterwaul-components/lib/Radio/Radios';
import React, { useEffect } from 'react';
import ShippingMethodInactive from './ShippingMethodInactive';
import ShippingMethodLabel from './ShippingMethodLabel';
import styled from 'styled-components';

export type ShippingMethodProps = {
    active: boolean;
    catalogId: number;
    laShipping: boolean;
    loading?: boolean;
    onReviewStep: boolean;
    onShippingMethodChange: (shippingMethod: ShippingMethods, shippingMethodValid: boolean) => void;
    sellerName: string;
    sellerUrl: string;
    shippingMethod: ShippingMethods;
    shippingMethodValid: boolean;
    shippingQuote: ShippingQuote;
};

const ShippingMethod = ({
    active,
    catalogId,
    laShipping,
    loading,
    onReviewStep,
    onShippingMethodChange,
    sellerName,
    sellerUrl,
    shippingMethod,
    shippingMethodValid,
    shippingQuote,
}: ShippingMethodProps) => {
    const { address1, city, postalCode, state } = useAppSelector((state) => getCatalogAddress(state, catalogId));
    const freeLocalPickup = useAppSelector((state) => getCatalogDetailFreeLocalPickup(state, catalogId));
    const inHouseShipping = useAppSelector((state) => getCatalogDetailInHouseShipping(state, catalogId));
    const hasBidderShippingAvailable = useAppSelector((state) =>
        getCatalogDetailHasBidderShippingAvailable(state, catalogId)
    );
    const { invoice, invoiceAddress, items } = useAppSelector((state) => getInvoiceById(state, catalogId, laShipping));
    const numberOfItems = items.length;

    const catalogFullAddress = `${address1}, ${city}, ${state}, ${postalCode}`;
    const catalogStreetAddress = `${address1}`;
    const catalogStateAddress = `, ${city}, ${state}, ${postalCode}`;

    const hasHouseShippingAvailable = (inHouseShipping || invoice.houseShippingQuote > 0) && !laShipping;

    const onRadioChange = (event: any) => {
        const { currentTarget } = event;
        const { name } = currentTarget;
        onShippingMethodChange(name, true);
    };

    useEffect(() => {
        if (!active) {
            return;
        }
        const hasInvalidLiveShippingOption = shippingMethod === ShippingMethods.LiveShipping && !laShipping;
        const hasInvalidHouseShippingOption =
            shippingMethod === ShippingMethods.HouseShipping && !hasHouseShippingAvailable;
        const hasInvalidBidderShippingOption =
            shippingMethod === ShippingMethods.BidderShipping && !hasBidderShippingAvailable;

        const needsToSelectFirstAvailableShippingOption =
            !Boolean(shippingMethod) ||
            hasInvalidLiveShippingOption ||
            hasInvalidHouseShippingOption ||
            hasInvalidBidderShippingOption;

        if (!needsToSelectFirstAvailableShippingOption) {
            return;
        }

        if (laShipping) {
            onShippingMethodChange(ShippingMethods.LiveShipping, true);
        } else if (hasHouseShippingAvailable) {
            onShippingMethodChange(ShippingMethods.HouseShipping, true);
        } else if (hasBidderShippingAvailable) {
            onShippingMethodChange(ShippingMethods.BidderShipping, true);
        } else {
            // At the time of writing, the pickup option will always be visible, thus the else statement
            onShippingMethodChange(ShippingMethods.Pickup, true);
        }
    }, [
        active,
        invoiceAddress,
        laShipping,
        onShippingMethodChange,
        shippingMethod,
        hasHouseShippingAvailable,
        hasBidderShippingAvailable,
    ]);

    if (!active) {
        return (
            <ShippingMethodInactive
                catalogId={catalogId}
                numberOfItems={numberOfItems}
                onReviewStep={onReviewStep}
                sellerName={sellerName}
                shippingMethod={shippingMethod}
                shippingMethodValid={shippingMethodValid}
                shippingQuote={shippingQuote}
            />
        );
    }

    return (
        <StyledShippingMethod>
            <StyledRadios
                onChange={onRadioChange}
                selectedName={shippingMethod}
            >
                {laShipping && (
                    <LiveShippingShippingMethod
                        loading={loading}
                        numberOfItems={numberOfItems}
                        shippingQuote={shippingQuote}
                    />
                )}
                {hasHouseShippingAvailable && (
                    <StyledRadio
                        disabled={loading}
                        label={
                            <ShippingMethodLabel
                                info={
                                    invoice.houseShippingQuote > 0 ? (
                                        <FormattedMessage
                                            id="checkout.shippingMethod.method.inHouseDescription"
                                            key="checkout-shipping-method-in-house-description"
                                            values={{ houseName: sellerName }}
                                        />
                                    ) : (
                                        <FormattedMessage
                                            id="checkout.shippingMethod.method.requestShippingQuote"
                                            key="checkout-shipping-method-request-shipping-quote"
                                            values={{
                                                houseLink: (
                                                    <Link
                                                        key="checkout-shipping-method-request-shipping-quote-house-link"
                                                        route
                                                        to={sellerUrl}
                                                    >
                                                        {sellerName}
                                                    </Link>
                                                ),
                                            }}
                                        />
                                    )
                                }
                                popoverContent={
                                    invoice.houseShippingQuote ? (
                                        <FormattedMessage
                                            id="checkout.shippingMethod.popovers.houseShip"
                                            key="checkout-shipping-method-popovers-house-ship"
                                            values={{ houseName: sellerName }}
                                        />
                                    ) : (
                                        <FormattedMessage
                                            id="checkout.shippingMethod.popovers.houseShipPending"
                                            key="checkout-shipping-method-popovers-house-ship-pending"
                                            values={{ houseName: sellerName }}
                                        />
                                    )
                                }
                                price={
                                    invoice.houseShippingQuote > 0 && (
                                        <FormattedNumber
                                            currency="USD"
                                            key="checkout-shipping-method-house-shipping-quote"
                                            maximumFractionDigits={2}
                                            minimumFractionDigits={2}
                                            style="currency"
                                            value={Number(invoice.houseShippingQuote)}
                                        />
                                    )
                                }
                                title={<FormattedMessage id="checkout.shippingMethod.method.H" />}
                            />
                        }
                        name={ShippingMethods.HouseShipping}
                        testid="shippingMethodHouseShipping"
                    />
                )}
                {hasBidderShippingAvailable && (
                    <StyledRadio
                        disabled={loading}
                        label={
                            <ShippingMethodLabel
                                info={<FormattedMessage id="checkout.shippingMethod.method.selfDescription" />}
                                popoverContent={<FormattedMessage id="checkout.shippingMethod.popovers.selfShip" />}
                                price={<FormattedMessage id="checkout.shippingMethod.method.pricesVary" />}
                                title={<FormattedMessage id="checkout.shippingMethod.method.self" />}
                            />
                        }
                        name={ShippingMethods.BidderShipping}
                        testid="shippingMethodBidderShipping"
                    />
                )}
                {freeLocalPickup && (
                    <StyledRadio
                        $noBottomBorder
                        disabled={loading}
                        label={
                            <ShippingMethodLabel
                                info={
                                    <FormattedMessage
                                        id="checkout.shippingMethod.method.pickupDescription"
                                        key="checkout-shipping-method-pickup-description"
                                        values={{
                                            locationState: catalogStateAddress,
                                            locationStreet: (
                                                <Link
                                                    key="checkout-shipping-method-pickup-location-street"
                                                    rel="noopener"
                                                    target="_blank"
                                                    to={`https://maps.google.com/maps?q=${catalogFullAddress}`}
                                                >
                                                    {catalogStreetAddress}
                                                </Link>
                                            ),
                                        }}
                                    />
                                }
                                popoverContent={
                                    <FormattedMessage
                                        id="checkout.shippingMethod.popovers.localPickup"
                                        key="checkout-shipping-method-popovers-local-pickup"
                                        values={{
                                            location: catalogFullAddress,
                                        }}
                                    />
                                }
                                price={<FormattedMessage id="checkout.shippingMethod.method.free" />}
                                title={<FormattedMessage id="checkout.shippingMethod.method.P" />}
                            />
                        }
                        name={ShippingMethods.Pickup}
                        testid="shippingMethodPickup"
                    />
                )}
            </StyledRadios>
        </StyledShippingMethod>
    );
};

export default ShippingMethod;

const StyledShippingMethod = styled.div`
    overflow: hidden;
    display: flex;
    flex-direction: row;
    position: relative;
`;

const StyledRadios = styled(Radios)`
    width: 100%;
    > *:not(:last-child) {
        margin-bottom: 24px;
    }
`;

const StyledRadio = styled(Radio).attrs(() => ({
    radioCircleClassName: 'radioCircleClassName',
}))<{ $noBottomBorder?: boolean }>`
    padding-left: 26px;
    .radioCircleClassName {
        top: 2px;
        left: 0;
    }

    & > span:first-of-type {
        width: 100%;
    }
`;
